import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, CheckCircle2, ShieldAlert, PlayCircle, Loader2, BadgeIndianRupee, Info, BadgeCheck, Clock, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatCard, StatusBadge, LoadingRows, TableShell, TR, TD } from "../../components/Shared";
import { Button } from "../../components/ui/button";

const MON_CHIP = { pending: "bg-amber-500/10 text-amber-600", active: "bg-emerald-500/10 text-emerald-600", rejected: "bg-red-500/10 text-red-500", suspended: "bg-zinc-500/10 text-zinc-500", off: "bg-muted text-muted-foreground" };
const MON_LABEL = { pending: "Pending Review", active: "Active", rejected: "Rejected", suspended: "Suspended", off: "Off" };

export const inr2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const MonChip = ({ status, reason }) => (
  <span title={reason || ""} className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${MON_CHIP[status] || MON_CHIP.off}`}>
    Monetization: {MON_LABEL[status] || "Off"}
  </span>
);

export default function CreatorMonetization() {
  const [data, setData] = useState(null);
  const [appStatus, setAppStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/vid/creator/monetization").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadStatus = () => api.get("/vid/creator/monetization/status").then((r) => setAppStatus(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); loadStatus(); }, []);

  const monetize = async (id) => {
    setBusy(id);
    try { await api.post(`/vid/creator/videos/${id}/monetize`); toast.success("Monetization requested — under review"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const apply = async () => {
    setBusy("apply");
    try { await api.post("/vid/creator/monetization/apply"); toast.success("Application submitted — the admin team will review it"); loadStatus(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (!data) return <LoadingRows rows={4} />;
  const t = data.totals;

  return (
    <div className="space-y-8" data-testid="creator-monetization-page">
      <PageHeader title="Monetization" subtitle="Earn from genuine eligible views on your videos — affiliate earnings stay separate" testid="monetization-title" />

      {appStatus && (
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="mon-application-card">
          {appStatus.approved ? (
            <div className="flex items-start gap-3" data-testid="mon-approved-banner">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-emerald-500/15"><BadgeCheck className="h-5 w-5 text-emerald-500" /></div>
              <div>
                <p className="font-display font-bold">Monetization Program: Active</p>
                <p className="text-xs text-muted-foreground mt-0.5">Your application was approved — monetize any published video below and earn per 1,000 eligible views.</p>
              </div>
            </div>
          ) : appStatus.application?.status === "pending" ? (
            <div className="flex items-start gap-3" data-testid="mon-pending-banner">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-amber-500/15"><Clock className="h-5 w-5 text-amber-500" /></div>
              <div>
                <p className="font-display font-bold">Application under review</p>
                <p className="text-xs text-muted-foreground mt-0.5">Your monetization application is being reviewed by the admin team. You'll be notified once approved.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-emerald-500/15"><BadgeIndianRupee className="h-5 w-5 text-emerald-500" /></div>
                <div>
                  <p className="font-display font-bold">Join the Monetization Program</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Meet the eligibility criteria and apply — the admin team reviews every application manually.</p>
                </div>
              </div>
              {appStatus.application?.status === "rejected" && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-xs font-semibold text-red-500" data-testid="mon-rejected-note">
                  <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
                  Your previous application was not approved{appStatus.application.reason ? ` — ${appStatus.application.reason}` : ""}. You can apply again once you meet the criteria.
                </div>
              )}
              <div className="space-y-2" data-testid="mon-criteria-list">
                {[["Total views", appStatus.stats.views, appStatus.criteria.min_views],
                  ["Followers", appStatus.stats.followers, appStatus.criteria.min_followers],
                  ["Published videos", appStatus.stats.videos, appStatus.criteria.min_videos]].map(([l, val, req]) => {
                  const met = req === 0 || val >= req;
                  return (
                    <div key={l} className="flex items-center justify-between rounded-xl border border-border px-4 py-2.5" data-testid={`mon-criteria-${l.split(" ")[0].toLowerCase()}`}>
                      <span className="text-sm font-semibold">{l}</span>
                      <span className={`text-xs font-bold flex items-center gap-1.5 ${met ? "text-emerald-600" : "text-amber-600"}`}>
                        {met ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                        {req === 0 ? `${Number(val).toLocaleString("en-IN")} · No minimum` : `${Number(val).toLocaleString("en-IN")} / ${Number(req).toLocaleString("en-IN")}`}
                      </span>
                    </div>
                  );
                })}
              </div>
              <Button data-testid="mon-apply-btn" disabled={!appStatus.eligible || busy === "apply"} onClick={apply}
                className="rounded-full font-bold gradient-bg text-white border-0">
                {busy === "apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : appStatus.eligible ? "Apply for Monetization" : "Not Eligible Yet — Keep Growing"}
              </Button>
            </>
          )}
        </div>
      )}

      {!data.enabled && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 text-sm" data-testid="mon-disabled-note">
          <Info className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <p>View-based monetization is currently <b>disabled</b> by the platform admin. Your affiliate earnings continue normally.</p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Views" value={t.total_views} testid="mon-total-views" tint="bg-blue-500/10 text-blue-500" />
        <StatCard icon={CheckCircle2} label="Eligible Views" value={t.eligible_views} testid="mon-eligible" delay={0.05} tint="bg-emerald-500/10 text-emerald-500" />
        <StatCard icon={ShieldAlert} label="Invalid / Fraud Views" value={t.invalid_views} testid="mon-invalid" delay={0.1} tint="bg-red-500/10 text-red-500" />
        <StatCard icon={PlayCircle} label="Monetized Views" value={t.monetized_views} testid="mon-monetized" delay={0.15} tint="bg-violet-500/10 text-violet-500" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Total Earnings", t.earn_total, "linear-gradient(135deg,#007A4D,#059669)"], ["Pending", t.earn_pending, "linear-gradient(135deg,#d97706,#f59e0b)"],
          ["Approved", t.earn_approved, "linear-gradient(135deg,#2563EB,#06B6D4)"], ["Paid", t.earn_paid, "linear-gradient(135deg,#7C3AED,#DB2777)"]].map(([l, v, bg]) => (
          <div key={l} data-testid={`mon-earn-${l.split(" ")[0].toLowerCase()}`} className="rounded-2xl p-4 text-white" style={{ background: bg }}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-white/75">{l}</p>
            <p className="font-display font-extrabold text-xl mt-1">{typeof v === "number" ? inr2(v) : v}</p>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Per-Video Monetization</h2>
        {appStatus && !appStatus.approved && (
          <div className="flex items-start gap-3 bg-muted/60 border border-border rounded-2xl p-4 text-sm mb-4" data-testid="mon-locked-note">
            <Lock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <p>Per-video monetization is <b>locked</b> until your Monetization Program application is approved.</p>
          </div>
        )}
        {data.videos.length === 0 ? (
          <div className="text-sm text-muted-foreground">No videos yet. <Link to="/creator/videos/new" className="font-bold underline">Add your first video</Link></div>
        ) : (
          <TableShell head={["Video", "Views", "Eligible", "Invalid", "Estimated", "Approved", "Monetization"]} testid="mon-videos-table">
            {data.videos.map((v) => {
              const m = v.monetization;
              return (
                <TR key={v.id} testid={`mon-video-${v.id}`}>
                  <TD>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-16 rounded-lg overflow-hidden shrink-0 bg-muted">
                        {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div className="min-w-0">
                        <Link to={`/v/${v.slug}`} className="font-semibold text-sm hover:text-primary transition-colors max-w-[200px] truncate block">{v.title}</Link>
                        <StatusBadge status={v.status} />
                      </div>
                    </div>
                  </TD>
                  <TD><span className="font-bold">{v.views}</span></TD>
                  <TD><span className="font-bold text-emerald-600">{m.eligible_views}</span></TD>
                  <TD><span className={m.invalid_views > 0 ? "font-bold text-red-500" : ""}>{m.invalid_views}</span></TD>
                  <TD><span className="font-bold">{inr2(m.earn_pending)}</span></TD>
                  <TD><span className="font-bold text-emerald-600">{inr2(m.earn_approved)}</span></TD>
                  <TD>
                    <div className="flex flex-col gap-1.5 items-start">
                      <MonChip status={m.status} reason={m.reason} />
                      {m.status === "off" && v.status === "published" && data.enabled && appStatus?.approved && (
                        <Button size="sm" variant="outline" data-testid={`mon-enable-${v.id}`} disabled={busy === v.id} onClick={() => monetize(v.id)}
                          className="rounded-full font-bold text-emerald-600 border-emerald-500/40">
                          {busy === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><BadgeIndianRupee className="h-3.5 w-3.5 mr-1" />Monetize This Video</>}
                        </Button>
                      )}
                      {m.reason && m.status === "rejected" && <p className="text-[10px] text-red-500 max-w-[180px]">{m.reason}</p>}
                    </div>
                  </TD>
                </TR>
              );
            })}
          </TableShell>
        )}
      </div>
    </div>
  );
}
